/// <reference path="../../../types/three-extended.d.ts" />
import {
    EventDispatcher,
    Object3D,
    Raycaster,
    Vector3,
    Quaternion,
    Matrix4,
    Sprite,
    Color,
    Clock,
    RingGeometry,
    Mesh,
    MeshBasicMaterial,
    Material,
    BufferGeometry,
    Vector2,
    Euler,
    Group,
} from 'three';
import { autoInjectable, singleton } from 'tsyringe';
import Renderer from '../Renderer';
import XrControllers from './XrControllers';
import linesHelper, { pointer } from './ControllersUtils';
import Config from '../../../Config';
import Camera from '../../Camera';
import { MeshBVH, SAH, StaticGeometryGenerator } from 'three-mesh-bvh';
import { Body, Vec3 } from 'cannon-es';
import { XRControllerModelFactory } from 'three/examples/jsm/webxr/XRControllerModelFactory';

interface GrabbableObject {
    object: Object3D;
    mode: 'rigid' | 'physics';
    grabOffset?: Vector3;
    grabRotation?: Quaternion;
    grabRotationRight?: Quaternion;
    originalParent?: Object3D;
    originalPosition?: Vector3;
    originalRotation?: Quaternion;
    physicsBody?: any;
    physicsGripConfig?: PhysicsGripConfig;
    onGrab?: (controller: Object3D) => void;
    onRelease?: (controller: Object3D) => void;
    onPositionUpdate?: (object: Object3D, proposedPosition: Vector3) => void;
    constraints?: {
        position?: boolean;
        rotation?: boolean;
    };
}

interface PhysicsGripConfig {
    mode: 'spring' | 'kinematic' | 'rigid' | 'test' | 'velocity';

    springStrength?: number;
    damping?: number;
    maxForce?: number;
    velocityStrength?: number;
    angularVelocityStrength?: number;
    maxVelocity?: number;

    torqueStrength?: number;
    lerpFactor?: number;

    maintainRotation?: boolean;
    maintainVelocity?: boolean;
}

export class GazeRing extends Mesh {
    public material: Material | Material[] = new MeshBasicMaterial({
        opacity: 0.5,
        transparent: true,
    });

    public geometry: BufferGeometry = new RingGeometry(0.02, 0.04, 32).translate(0, 0, -1);

    public constructor() {
        super();
        this.geometry = new RingGeometry(0.02, 0.04, 32).translate(0, 0, -1);
        this.material = new MeshBasicMaterial({
            opacity: 0.5,
            transparent: true,
            color: new Color('white'),
        });
    }

    public setDefault() {
        this.geometry.dispose();
        this.geometry = new RingGeometry(0.02, 0.04, 32).translate(0, 0, -1);
    }

    public updateProgress(progress: number) {
        const thetaLettingTime = Math.round(progress * 100) * ((Math.PI * 2) / 100);
        this.geometry.dispose();
        this.geometry = new RingGeometry(0.02, 0.04, 32, 32, 0, thetaLettingTime).translate(0, 0, -1);
    }
}

interface InteractiveObject {
    object: Object3D;
    priority: number;
    onHoverStart?: (controller: Object3D, intersection: IntersectionResult) => void;
    onHoverEnd?: (controller: Object3D, intersection: IntersectionResult) => void;
    onHover?: (controller: Object3D, intersection: IntersectionResult) => void;
    onSelectStart?: (controller: Object3D, intersection: IntersectionResult) => void;
    onSelectEnd?: (controller: Object3D, intersection: IntersectionResult) => void;
    onRotate?: (rotation: Quaternion, controller: Object3D, intersection: IntersectionResult) => void;
}

interface IntersectionResult {
    point: Vector3;
    distance: number;
    object: Object3D;
}

interface XRGamepadButton {
    pressed: boolean;
    touched: boolean;
    value: number;
}

interface XRGamepad {
    buttons: XRGamepadButton[];
    axes: number[];
    connected: boolean;
    mapping: string;
}

export interface InteractionManagerEventMap {
    gamepadThumbstick: { x: number, y: number, controller: Object3D };
    gamepadButton: { button: string, action: string, controller: Object3D };
    controllerActivated: { controller: Object3D };
    controllerDeactivated: {};
}

@singleton()
@autoInjectable()
export class InteractionManager extends EventDispatcher<InteractionManagerEventMap> {
    private interactiveObjects: InteractiveObject[] = [];
    private hoveredObjects: Map<Object3D, InteractiveObject> = new Map();
    private selectedObjects: Map<Object3D, InteractiveObject> = new Map();
    private raycaster: Raycaster = new Raycaster();
    private tempMatrix = new Matrix4();
    public pointer: Sprite = pointer;
    private renderer?: Renderer = null;

    public controllers: Object3D[] = [];

    private gazeRing?: GazeRing;
    private intersectionClock: Clock;
    private readonly GAZE_SELECTION_TIME = 2;
    private isUsingGazeController = false;
    private gazeController: Object3D | null = null;

    private readonly handleSelectStart: (event: any) => void;
    private readonly handleSelectEnd: (event: any) => void;
    private readonly handleSceneChange: () => void;

    private activeController: Object3D | null = null;
    private readonly ACTIVE_COLOR = new Color('lightblue');
    private readonly INACTIVE_COLOR = new Color('white');
    private readonly INTERSECTION_COLOR = new Color('blue');

    private mousePosition = new Vector2();
    private isMouseHovering = false;
    private mouseHoveredObject: InteractiveObject | null = null;
    private mouseSelectedObject: InteractiveObject | null = null;
    private canvas: HTMLCanvasElement | null = null;

    private readonly handleMouseMove: (event: MouseEvent) => void;
    private readonly handleMouseDown: (event: MouseEvent) => void;
    private readonly handleMouseUp: (event: MouseEvent) => void;

    private activeGamepads: Map<Object3D, XRGamepad> = new Map();
    private buttonStates: Map<Object3D, boolean[]> = new Map();
    private readonly AXIS_THRESHOLD = 0.1;

    private isHoldingObject: boolean = false;
    private heldObject: Object3D | null = null;

    private bvhManager: MeshBVH;

    private holdingController: Object3D | null = null;

    private grabbedObjects: Map<Object3D, GrabbableObject> = new Map();
    private grabOffset: Map<Object3D, Vector3> = new Map();
    private grabRotationOffset: Map<Object3D, Quaternion> = new Map();
    private tempVector = new Vector3();
    private tempQuaternion = new Quaternion();
    private virtualMouseController: Object3D | null = null;

    private previousMousePositions: Map<Object3D, Vector3> = new Map();
    private mouseVelocities: Map<Object3D, Vector3> = new Map();
    private lastUpdateTime: number = performance.now();

    private mouseWheelDelta: number = 0;
    private objectDistance: Map<Object3D, number> = new Map();

    private objectPreviousPositions: Map<Object3D, Vector3> = new Map();
    private objectVelocities: Map<Object3D, Vector3> = new Map();
    private objectLastUpdateTime: Map<Object3D, number> = new Map();

    private controllerPreviousPositions: Map<Object3D, Vector3> = new Map();
    private controllerLastUpdateTime: Map<Object3D, number> = new Map();

    private keyStates: Map<string, boolean> = new Map();
    private readonly keyboardMoveSpeed: number = 3.0;
    private clock: Clock = new Clock();

    private readonly handleKeyDown: (event: KeyboardEvent) => void;
    private readonly handleKeyUp: (event: KeyboardEvent) => void;

    public controllerGrips: Object3D[] = [];
    private controllerGripLeft: Group | null = null;
    private controllerGripRight: Group | null = null;

    public controllerListeners = false;

    public xrControllers?: XrControllers;

    private isHandlingMouseInteraction: boolean = false;

    private lastInteractionTime: number = 0;
    private readonly INTERACTION_BLOCK_TIME = 500;

    public constructor(public config?: Config, public camera?: Camera) {
        super();

        this.handleSelectStart = this.onSelectStart.bind(this);
        this.handleSelectEnd = this.onSelectEnd.bind(this);
        this.handleSceneChange = this.onSceneChange.bind(this);

        this.handleMouseMove = this.onMouseMove.bind(this);
        this.handleMouseDown = this.onMouseDown.bind(this);
        this.handleMouseUp = this.onMouseUp.bind(this);

        this.handleKeyDown = this.onKeyDown.bind(this);
        this.handleKeyUp = this.onKeyUp.bind(this);

        this.canvas = document.querySelector('.game .canvas');

        this.intersectionClock = new Clock(false);
        this.gazeRing = new GazeRing();
        this.gazeRing.visible = false;
    }

    public setupBVH(scene: Object3D) {
        const staticGenerator = new StaticGeometryGenerator(scene);
        const staticGeometry = staticGenerator.generate();

        staticGeometry.boundsTree = new MeshBVH(staticGeometry);
        this.raycaster.firstHitOnly = true;
    }

    public addGrabbableObject(
        object: Object3D,
        mode: 'rigid' | 'physics' = 'rigid',
        options?: {
            onGrab?: (controller: Object3D) => void;
            onRelease?: (controller: Object3D) => void;
            onPositionUpdate?: (object: Object3D, proposedPosition: Vector3) => void;
            constraints?: {
                position?: boolean;
                rotation?: boolean;
            };
            physicsBody?: any;
            physicsGripConfig?: PhysicsGripConfig;
            addChildren?: boolean;
            grabRotation?: Euler;
            grabRotationRight?: Euler;
        },
    ): void {
        let physicsBody = options?.physicsBody;
        if (!physicsBody && (object as any).physicsBody) {
            physicsBody = (object as any).physicsBody;
        }

        const defaultPhysicsConfig: PhysicsGripConfig = {
            mode: 'spring',
            springStrength: 50,
            damping: 10,
            maxForce: 100,
            lerpFactor: 0.15,
            velocityStrength: 30,
            angularVelocityStrength: 20,
            maintainRotation: true,
            maintainVelocity: true,
        };
        let grabRotationQuat: Quaternion | undefined;
        let grabRotationRightQuat: Quaternion | undefined;

        if (options?.grabRotation) {
            grabRotationQuat = new Quaternion().setFromEuler(options.grabRotation);
        }

        if (options?.grabRotationRight) {
            grabRotationRightQuat = new Quaternion().setFromEuler(options.grabRotationRight);
        }

        const grabbableObject: GrabbableObject = {
            object,
            mode,
            physicsBody,
            grabRotation: grabRotationQuat,
            grabRotationRight: grabRotationRightQuat,
            physicsGripConfig:
                mode === 'physics' ? { ...defaultPhysicsConfig, ...options?.physicsGripConfig } : undefined,
            constraints: {
                position: true,
                rotation: true,
                ...options?.constraints,
            },
            onGrab: options?.onGrab,
            onRelease: options?.onRelease,
            originalParent: object.parent,
            originalPosition: object.position.clone(),
            originalRotation: object.quaternion.clone(),
            onPositionUpdate: options?.onPositionUpdate,
        };

        this.addInteractiveObject(
            object,
            10,
            {
                onSelectStart: (controller, intersection) => {
                    this.grabObject(controller, grabbableObject, intersection);
                },
                onSelectEnd: (controller) => {
                    this.releaseObject(controller);
                },
                onHover: (controller, intersection) => {
                    if (this.grabbedObjects.has(controller)) {
                        this.updateGrabbedObjectPosition(controller);
                    }
                },
            },
            options?.addChildren,
        );
    }

    private grabObject(controller: Object3D, grabbableObject: GrabbableObject, intersection: IntersectionResult): void {
        const currentGrabbingController = this.getGrabbingController(grabbableObject.object);
        if (currentGrabbingController && currentGrabbingController !== controller) {
            this.releaseObject(currentGrabbingController);
        }
        if (this.grabbedObjects.has(controller)) {
            this.releaseObject(controller);
        }

        grabbableObject.object.userData.isGrabbed = true;
        this.camera?.disableOrbitControls();

        const object = grabbableObject.object;

        if (!controller.userData?.isVirtualMouseController) {
            this.hideController(controller);

            this.grabOffset.set(controller, new Vector3(0, 0, 0));

            const controllerWorldQuat = new Quaternion();
            controller.getWorldQuaternion(controllerWorldQuat);
            if (grabbableObject.grabRotation || grabbableObject.grabRotationRight) {
                const isLeftHanded = this.isLeftController(controller);

                let finalGrabRotation: Quaternion;

                if (isLeftHanded) {
                    if (grabbableObject.grabRotation) {
                        finalGrabRotation = grabbableObject.grabRotation.clone();
                    } else {
                        finalGrabRotation = grabbableObject.grabRotationRight!.clone();
                    }
                } else {
                    if (grabbableObject.grabRotationRight) {
                        finalGrabRotation = grabbableObject.grabRotationRight.clone();
                    } else {
                        finalGrabRotation = grabbableObject.grabRotation!.clone();
                    }
                }

                this.tempQuaternion.copy(controllerWorldQuat).invert().multiply(finalGrabRotation);
            } else {
                const objectWorldQuaternion = new Quaternion();
                object.getWorldQuaternion(objectWorldQuaternion);
                this.tempQuaternion.copy(controllerWorldQuat).invert().multiply(objectWorldQuaternion);
            }

            this.grabRotationOffset.set(controller, this.tempQuaternion.clone());
        } else {
            const objectWorldPosition = new Vector3();
            object.getWorldPosition(objectWorldPosition);
            const offset = objectWorldPosition.sub(intersection.point);
            this.grabOffset.set(controller, offset);
        }

        this.grabbedObjects.set(controller, grabbableObject);
        grabbableObject.onGrab?.(controller);

        if (grabbableObject.mode === 'physics') {
            const currentPos = new Vector3();
            object.getWorldPosition(currentPos);
            this.objectPreviousPositions.set(controller, currentPos.clone());
            this.objectVelocities.set(controller, new Vector3(0, 0, 0));
            this.objectLastUpdateTime.set(controller, performance.now());
        }

        this.highlightGrabbedObject(object, true);
    }

    private isLeftController(controller: Object3D): boolean {
        if (controller.userData.handedness) {
            return controller.userData.handedness === 'left';
        }

        const controllerIndex = this.controllers.indexOf(controller);
        if (controllerIndex !== -1) {
            return controllerIndex === 0;
        }

        const worldPos = new Vector3();
        controller.getWorldPosition(worldPos);
        return worldPos.x < 0;
    }

    private releaseObject(controller: Object3D): void {
        this.camera?.enableOrbitControls();

        this.showController(controller);

        const grabbedObject = this.grabbedObjects.get(controller);
        if (!grabbedObject) {
            return;
        }

        const object = grabbedObject.object;
        object.userData.isGrabbed = false;

        if (grabbedObject.physicsBody) {
            grabbedObject.physicsBody.type = Body.DYNAMIC;
            grabbedObject.physicsBody.collisionResponse = true;

            const worldPosition = new Vector3();
            const worldQuaternion = new Quaternion();
            object.getWorldPosition(worldPosition);
            object.getWorldQuaternion(worldQuaternion);

            grabbedObject.physicsBody.position.set(worldPosition.x, worldPosition.y, worldPosition.z);
            grabbedObject.physicsBody.quaternion.set(
                worldQuaternion.x,
                worldQuaternion.y,
                worldQuaternion.z,
                worldQuaternion.w,
            );

            grabbedObject.physicsBody.force.set(0, 0, 0);
            grabbedObject.physicsBody.torque.set(0, 0, 0);

            if (grabbedObject.physicsBody.mass <= 0) {
                grabbedObject.physicsBody.mass = 1;
            }

            if (grabbedObject.physicsGripConfig?.maintainVelocity !== false) {
                if (controller.userData?.isVirtualMouseController) {
                    const objectVelocity = this.objectVelocities.get(controller);

                    if (objectVelocity && objectVelocity.length() > 0.1) {
                        const throwMultiplier = 1.5;
                        const throwVelocity = objectVelocity.clone().multiplyScalar(throwMultiplier);

                        const maxVelocity = 30;
                        if (throwVelocity.length() > maxVelocity) {
                            throwVelocity.normalize().multiplyScalar(maxVelocity);
                        }

                        grabbedObject.physicsBody.velocity.x = throwVelocity.x;
                        grabbedObject.physicsBody.velocity.y = throwVelocity.y;
                        grabbedObject.physicsBody.velocity.z = throwVelocity.z;
                    } else {
                        grabbedObject.physicsBody.velocity.set(0, 0, 0);
                    }
                } else if (controller.userData.velocity) {
                    grabbedObject.physicsBody.velocity.copy(controller.userData.velocity);
                } else {
                    grabbedObject.physicsBody.velocity.set(0, 0, 0);
                }
            } else {
                grabbedObject.physicsBody.velocity.set(0, 0, 0);
                grabbedObject.physicsBody.angularVelocity.set(0, 0, 0);
            }

            if (grabbedObject.physicsBody.world) {
                grabbedObject.physicsBody.wakeUp();
            }
        }

        grabbedObject.onRelease?.(controller);
        this.highlightGrabbedObject(object, false);

        this.grabbedObjects.delete(controller);
        this.grabOffset.delete(controller);
        this.grabRotationOffset.delete(controller);
        this.objectDistance.delete(controller);
        this.previousMousePositions.delete(controller);
        this.mouseVelocities.delete(controller);
        this.objectPreviousPositions.delete(controller);
        this.objectVelocities.delete(controller);
        this.objectLastUpdateTime.delete(controller);
    }

    public setGrabMode(controller: Object3D, mode: 'spring' | 'kinematic' | 'rigid'): void {
        const grabbedObject = this.grabbedObjects.get(controller);
        if (grabbedObject && grabbedObject.physicsGripConfig) {
            grabbedObject.physicsGripConfig.mode = mode;
        }
    }

    public getGrabbedObject(controller: Object3D): GrabbableObject | undefined {
        return this.grabbedObjects.get(controller);
    }

    private updateGrabbedObjectPosition(controller: Object3D): void {
        const grabbedObject = this.grabbedObjects.get(controller);
        if (!grabbedObject) return;

        const object = grabbedObject.object;
        const offset = this.grabOffset.get(controller);
        const rotationOffset = this.grabRotationOffset.get(controller);

        if (grabbedObject.mode === 'rigid') {
            if (controller.userData?.isVirtualMouseController) {
                this.updateMouseRigidObjectPosition(controller, object, offset);
            } else {
                controller.getWorldPosition(this.tempVector);
                if (offset) {
                    const worldOffset = offset.clone();
                    controller.getWorldQuaternion(this.tempQuaternion);
                    worldOffset.applyQuaternion(this.tempQuaternion);
                    this.tempVector.add(worldOffset);
                }

                if (object.parent && object.parent !== this.renderer?.scene) {
                    object.parent.worldToLocal(this.tempVector);
                }

                if (grabbedObject.onPositionUpdate) {
                    grabbedObject.onPositionUpdate(object, this.tempVector);
                }

                if (grabbedObject.constraints) {
                    const currentPos = object.position.clone();

                    if (grabbedObject.constraints.position) {
                    } else {
                        object.position.copy(this.tempVector);
                    }

                    if (grabbedObject.constraints.rotation) {
                    } else {
                        if (rotationOffset) {
                            controller.getWorldQuaternion(this.tempQuaternion);
                            this.tempQuaternion.multiply(rotationOffset);
                            object.quaternion.copy(this.tempQuaternion);
                        }
                    }
                } else {
                    object.position.copy(this.tempVector);
                    if (rotationOffset) {
                        controller.getWorldQuaternion(this.tempQuaternion);
                        this.tempQuaternion.multiply(rotationOffset);
                        object.quaternion.copy(this.tempQuaternion);
                    }
                }
            }
        } else if (grabbedObject.mode === 'physics' && grabbedObject.physicsBody) {
            if (controller.userData?.isVirtualMouseController) {
                this.updateMousePhysicsObjectPosition(controller, grabbedObject, offset);
            } else {
                this.updateVRPhysicsObjectPosition(controller, grabbedObject, offset, rotationOffset);
            }

            object.position.copy(grabbedObject.physicsBody.position as any);
            object.quaternion.copy(grabbedObject.physicsBody.quaternion as any);
        }

        this.calculateObjectVelocity(controller, object);
    }

    private constrainPositionToFloor(position: Vector3): Vector3 {
        const floorLevel = -2.5;
        const objectHalfHeight = 0.5;

        const targetObjectBottomY = position.y - objectHalfHeight;

        if (targetObjectBottomY < floorLevel) {
            position.y = floorLevel + objectHalfHeight;
        }

        return position;
    }

    private updateVRPhysicsObjectPosition(
        controller: Object3D,
        grabbableObject: GrabbableObject,
        offset?: Vector3,
        rotationOffset?: Quaternion,
    ): void {
        if (!grabbableObject.physicsBody) return;

        const gripConfig = grabbableObject.physicsGripConfig || { mode: 'spring' };
        const controllerWorldPos = new Vector3();
        controller.getWorldPosition(controllerWorldPos);

        const controllerWorldQuat = new Quaternion();
        controller.getWorldQuaternion(controllerWorldQuat);
        const targetPos = controllerWorldPos.clone();
        if (offset) {
            const worldOffset = offset.clone().applyQuaternion(controllerWorldQuat);
            targetPos.add(worldOffset);
        }
        let targetQuat = new Quaternion();
        if (gripConfig.maintainRotation && rotationOffset) {
            targetQuat.copy(controllerWorldQuat).multiply(rotationOffset);
        }

        switch (gripConfig.mode) {
            case 'rigid':
            case 'kinematic':
                grabbableObject.physicsBody.type = Body.KINEMATIC;
                grabbableObject.physicsBody.position.copy(targetPos as any);
                if (gripConfig.maintainRotation && rotationOffset) {
                    grabbableObject.physicsBody.quaternion.copy(targetQuat as any);
                }
                break;

            case 'velocity':
                grabbableObject.physicsBody.type = Body.DYNAMIC;
                grabbableObject.physicsBody.wakeUp();

                const currentPos = grabbableObject.physicsBody.position;
                const positionDiff = new Vec3(
                    targetPos.x - currentPos.x,
                    targetPos.y - currentPos.y,
                    targetPos.z - currentPos.z,
                );

                const velocityStrength = gripConfig.velocityStrength || 50;
                const targetVelocity = positionDiff.scale(velocityStrength);

                const maxVelocity = gripConfig.maxVelocity || 30;
                if (targetVelocity.lengthSquared() > maxVelocity * maxVelocity) {
                    targetVelocity.normalize();
                    targetVelocity.scale(maxVelocity, targetVelocity);
                }
                grabbableObject.physicsBody.velocity.copy(targetVelocity);

                if (gripConfig.maintainRotation && rotationOffset) {
                    grabbableObject.physicsBody.quaternion.copy(targetQuat as any);
                    grabbableObject.physicsBody.angularVelocity.set(0, 0, 0);
                }
                break;
        }
    }

    private updateMousePhysicsObjectPosition(
        controller: Object3D,
        grabbableObject: GrabbableObject,
        offset?: Vector3,
    ): void {
        if (!this.camera || !grabbableObject.physicsBody) return;

        const gripConfig = grabbableObject.physicsGripConfig || { mode: 'spring' };

        this.raycaster.setFromCamera(this.mousePosition, this.camera.instance);

        let distanceToObject = this.objectDistance.get(controller);
        if (!distanceToObject) {
            const cameraPosition = new Vector3();
            this.camera.instance.getWorldPosition(cameraPosition);
            const currentObjectPos = new Vector3(
                grabbableObject.physicsBody.position.x,
                grabbableObject.physicsBody.position.y,
                grabbableObject.physicsBody.position.z,
            );
            distanceToObject = cameraPosition.distanceTo(currentObjectPos);
            this.objectDistance.set(controller, distanceToObject);
        }

        const targetMousePos = this.raycaster.ray.origin
            .clone()
            .add(this.raycaster.ray.direction.clone().multiplyScalar(distanceToObject));

        if (offset) {
            targetMousePos.add(offset);
        }

        switch (gripConfig.mode) {
            case 'rigid':
                grabbableObject.physicsBody.type = Body.KINEMATIC;

                const constrainedPosition = this.constrainPositionToFloor(targetMousePos);
                grabbableObject.physicsBody.position.copy(constrainedPosition);

                break;
            case 'velocity':
                grabbableObject.physicsBody.type = Body.DYNAMIC;

                const currentPoss = grabbableObject.physicsBody.position;

                const positionDifff = new Vec3(
                    targetMousePos.x - currentPoss.x,
                    targetMousePos.y - currentPoss.y,
                    targetMousePos.z - currentPoss.z,
                );

                const velocityStrength = gripConfig.velocityStrength || 50;
                grabbableObject.physicsBody.velocity.copy(positionDifff.scale(velocityStrength));
                grabbableObject.physicsBody.angularVelocity.set(0, 0, 0);
                break;
            case 'kinematic':
                grabbableObject.physicsBody.type = Body.KINEMATIC;
                const lerpFactor = gripConfig.lerpFactor || 0.15;
                grabbableObject.physicsBody.position.lerp(targetMousePos, lerpFactor);
                break;

            case 'spring':
            default:
                grabbableObject.physicsBody.type = Body.DYNAMIC;

                const currentPos = grabbableObject.physicsBody.position;
                const positionDiff = new Vector3(
                    targetMousePos.x - currentPos.x,
                    targetMousePos.y - currentPos.y,
                    targetMousePos.z - currentPos.z,
                );

                const springStrength = gripConfig.springStrength || 50;
                const damping = gripConfig.damping || 10;
                const maxForce = gripConfig.maxForce || 100;

                const springForce = positionDiff.multiplyScalar(springStrength);
                const velocity = grabbableObject.physicsBody.velocity;
                const dampingForce = new Vector3(-velocity.x * damping, -velocity.y * damping, -velocity.z * damping);

                const totalForce = springForce.add(dampingForce);

                if (totalForce.length() > maxForce) {
                    totalForce.normalize().multiplyScalar(maxForce);
                }

                grabbableObject.physicsBody.force.x += totalForce.x;
                grabbableObject.physicsBody.force.y += totalForce.y;
                grabbableObject.physicsBody.force.z += totalForce.z;
                break;
        }
    }

    private calculateObjectVelocity(controller: Object3D, object: Object3D): void {
        const currentTime = performance.now();
        const lastTime = this.objectLastUpdateTime.get(controller);

        if (!lastTime) {
            this.objectLastUpdateTime.set(controller, currentTime);
            this.objectPreviousPositions.set(controller, new Vector3());
            this.objectVelocities.set(controller, new Vector3());
            return;
        }

        const deltaTime = (currentTime - lastTime) / 1000;
        if (deltaTime <= 0 || deltaTime > 1) return;

        const currentPos = new Vector3();
        object.getWorldPosition(currentPos);

        const previousPos = this.objectPreviousPositions.get(controller);
        if (!previousPos) {
            this.objectPreviousPositions.set(controller, currentPos.clone());
            return;
        }

        const rawVelocity = new Vector3().subVectors(currentPos, previousPos).divideScalar(deltaTime);

        const existingVelocity = this.objectVelocities.get(controller);
        if (existingVelocity) {
            const smoothingFactor = 0.2;
            rawVelocity.lerp(existingVelocity, smoothingFactor);
        }

        this.objectVelocities.set(controller, rawVelocity);
        this.objectPreviousPositions.set(controller, currentPos.clone());
        this.objectLastUpdateTime.set(controller, currentTime);
    }

    private updateMouseRigidObjectPosition(controller: Object3D, object: Object3D, offset?: Vector3): void {
        if (!this.camera) return;

        this.raycaster.setFromCamera(this.mousePosition, this.camera.instance);

        let distanceToObject = this.objectDistance.get(controller);
        if (!distanceToObject) {
            const cameraPosition = new Vector3();
            this.camera.instance.getWorldPosition(cameraPosition);
            const objectPosition = new Vector3();
            object.getWorldPosition(objectPosition);
            distanceToObject = cameraPosition.distanceTo(objectPosition);
            this.objectDistance.set(controller, distanceToObject);
        }

        const newPosition = this.raycaster.ray.origin
            .clone()
            .add(this.raycaster.ray.direction.clone().multiplyScalar(distanceToObject));

        if (offset) {
            newPosition.add(offset);
        }

        if (object.parent && object.parent !== this.renderer?.scene) {
            object.parent.worldToLocal(newPosition);
        }

        const grabbedObject = this.grabbedObjects.get(controller);

        if (grabbedObject?.onPositionUpdate) {
            grabbedObject.onPositionUpdate(object, newPosition);
        }

        if (grabbedObject?.constraints?.position) {
            const currentPos = object.position.clone();
            object.position.set(newPosition.x, currentPos.y, currentPos.z);
        } else {
            object.position.copy(newPosition);
        }
    }

    private highlightGrabbedObject(object: Object3D, grabbed: boolean): void {
        object.traverse((child) => {
            if (child.type === 'Mesh') {
                const mesh = child as Mesh;
                if (mesh.material) {
                    if (Array.isArray(mesh.material)) {
                        mesh.material.forEach((mat) => {
                            if ('emissive' in mat) {
                                (mat as any).emissive.setHex(grabbed ? 0x444444 : 0x000000);
                            }
                        });
                    } else {
                        if ('emissive' in mesh.material) {
                            (mesh.material as any).emissive.setHex(grabbed ? 0x444444 : 0x000000);
                        }
                    }
                }
            }
        });
    }

    public setRenderer(renderer: Renderer): void {
        this.renderer = renderer;

        if (!this.controllerListeners) {
            this.setupControllerListeners();
            this.controllerListeners = true;
        }

        this.setupMouseListeners();

        this.renderer.addEventListener('sceneChange', this.handleSceneChange);
    }

    private setupMouseListeners(): void {
        if (!this.canvas) return;

        this.canvas.addEventListener('mousemove', this.handleMouseMove);
        this.canvas.addEventListener('mousedown', this.handleMouseDown);
        this.canvas.addEventListener('mouseup', this.handleMouseUp);

        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);

        this.canvas.addEventListener('wheel', (event) => {
            event.preventDefault();

            for (const [controller, grabbedObject] of this.grabbedObjects.entries()) {
                if (controller.userData?.isVirtualMouseController) {
                    const currentDistance = this.objectDistance.get(controller) || 5;
                    const newDistance = Math.max(1, Math.min(20, currentDistance + event.deltaY * 0.01));
                    this.objectDistance.set(controller, newDistance);
                    break;
                }
            }
        });
    }

    private onKeyDown(event: KeyboardEvent): void {
        this.keyStates.set(event.key.toLowerCase(), true);
    }

    private onKeyUp(event: KeyboardEvent): void {
        this.keyStates.set(event.key.toLowerCase(), false);
    }

    private onMouseMove(event: MouseEvent): void {
        if (!this.canvas || !this.camera) return;

        const rect = this.canvas.getBoundingClientRect();
        this.mousePosition.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        this.mousePosition.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const intersection = this.getMousePointedObject();

        if (this.mouseHoveredObject?.object !== intersection?.object?.object) {
            if (this.mouseHoveredObject) {
                const intersectionResult = this.createMouseIntersectionResult(intersection);
                this.mouseHoveredObject.onHoverEnd?.(this.createVirtualMouseController(), intersectionResult);
            }

            if (intersection?.object) {
                const intersectionResult = this.createMouseIntersectionResult(intersection);
                intersection.object.onHoverStart?.(this.createVirtualMouseController(), intersectionResult);
            }

            this.mouseHoveredObject = intersection?.object || null;
        }

        if (intersection?.object) {
            const intersectionResult = this.createMouseIntersectionResult(intersection);
            intersection.object.onHover?.(this.createVirtualMouseController(), intersectionResult);
        }
    }

    private onMouseDown(event: MouseEvent): void {
        if (event.button !== 0) return;

        const intersection = this.getMousePointedObject();
        if (intersection?.object) {
            const obj = intersection.object.object;
            let isReallyInteractive = false;

            let current = obj;
            while (current) {
                if (current.userData?.isButton || current.userData?.isGrabbed || current.userData?.isInteractive) {
                    isReallyInteractive = true;
                    break;
                }
                current = current.parent;
            }

            if (isReallyInteractive) {
                this.lastInteractionTime = Date.now();
            }

            this.mouseSelectedObject = intersection.object;
            const intersectionResult = this.createMouseIntersectionResult(intersection);

            intersection.object.onSelectStart?.(this.createVirtualMouseController(), intersectionResult);
        }
    }

    public canTeleport(): boolean {
        if (this.lastInteractionTime === 0) {
            return true;
        }

        const timeSinceInteraction = Date.now() - this.lastInteractionTime;
        const canTeleport = timeSinceInteraction > this.INTERACTION_BLOCK_TIME;

        if (!canTeleport) {
            // console.log( Teleport blocked, time remaining:', this.INTERACTION_BLOCK_TIME - timeSinceInteraction, 'ms');
        }

        return canTeleport;
    }

    private onMouseUp(event: MouseEvent): void {
        if (event.button !== 0) return;

        const virtualController = this.createVirtualMouseController();

        let wasHoldingSomething = false;
        for (const [controller, grabbedObject] of this.grabbedObjects.entries()) {
            if (controller.userData?.isVirtualMouseController) {
                this.releaseObject(controller);
                wasHoldingSomething = true;
                break;
            }
        }

        if (!wasHoldingSomething && this.mouseSelectedObject) {
            const intersection = this.getMousePointedObject();
            const intersectionResult = intersection
                ? this.createMouseIntersectionResult(intersection)
                : {
                    point: new Vector3(),
                    distance: 0,
                    object: this.mouseSelectedObject.object,
                };

            this.mouseSelectedObject.onSelectEnd?.(virtualController, intersectionResult);
        }

        this.mouseSelectedObject = null;
        this.isHandlingMouseInteraction = false;
    }
    public isHandlingMouse(): boolean {
        return this.isHandlingMouseInteraction;
    }

    private getMousePointedObject(): any {
        if (!this.camera) return null;

        this.raycaster.setFromCamera(this.mousePosition, this.camera.instance);

        const objectsToTest = this.interactiveObjects
            .filter((io) => io && io.object && io.object.userData && !io.object.userData.isNonInteractive)
            .map((io) => io.object);

        const intersections = this.raycaster.intersectObjects(objectsToTest, true);

        if (intersections.length === 0) {
            return null;
        }

        for (const inter of intersections) {
            const foundIO = this.interactiveObjects.find((io) => {
                if (!io || !io.object) return false;

                return (
                    inter.object === io.object ||
                    io.object === inter.object.parent ||
                    io.object === inter.object.parent?.parent
                );
            });

            if (foundIO) {
                return {
                    point: inter.point.clone(),
                    distance: inter.distance,
                    object: foundIO,
                    rawIntersection: inter,
                };
            }
        }

        return null;
    }

    private createVirtualMouseController(): Object3D {
        if (!this.virtualMouseController) {
            this.virtualMouseController = new Object3D();
            this.virtualMouseController.userData.isVirtualMouseController = true;
        }

        if (this.camera) {
            this.raycaster.setFromCamera(this.mousePosition, this.camera.instance);
            this.virtualMouseController.position.copy(this.raycaster.ray.origin);
            this.virtualMouseController.quaternion.copy(this.camera.instance.quaternion);
        }

        return this.virtualMouseController;
    }

    private createMouseIntersectionResult(intersection: any): IntersectionResult {
        return {
            point: intersection?.point || new Vector3(),
            distance: intersection?.distance || 0,
            object: intersection?.rawIntersection?.object || intersection?.object?.object || null,
        };
    }

    public getHoldingController(): Object3D | null {
        return this.holdingController;
    }

    private setupControllerListeners(): void {
        if (!this.renderer?.webGLRenderer) {
            return;
        }

        const xrManager = this.renderer.webGLRenderer.xr;
        if (!xrManager) {
            return;
        }
        this.controllers = [xrManager.getController(0), xrManager.getController(1)];
        this.controllerGrips = [xrManager.getControllerGrip(0), xrManager.getControllerGrip(1)];

        this.controllerGripLeft = this.controllerGrips[0] as Group;
        this.controllerGripRight = this.controllerGrips[1] as Group;

        const controllerModelFactory = new XRControllerModelFactory() || null;

        if (controllerModelFactory) {
            this.controllerGrips.forEach((grip) => {
                //@ts-ignore

                const model = controllerModelFactory.createControllerModel(grip);
                if (model) {
                    grip.add(model);
                }
            });
        }

        this.controllers.forEach((controller, index) => {
            controller.addEventListener('connected' as any, (event: any) => {
                const targetRayMode = event.data.targetRayMode;

                if (event.data.handedness) {
                    controller.userData.handedness = event.data.handedness;

                    if (this.controllerGrips[index]) {
                        this.controllerGrips[index].userData.handedness = event.data.handedness;
                    }
                }

                if (event.data.gamepad) {
                    this.initializeGamepad(controller, event.data.gamepad);
                }

                if (targetRayMode === 'gaze') {
                    this.isUsingGazeController = true;
                    this.gazeController = controller;
                    if (this.gazeRing) {
                        this.gazeRing.visible = true;
                        this.camera.instance.add(this.gazeRing);
                    }
                } else if (targetRayMode === 'tracked-pointer') {
                    this.isUsingGazeController = false;
                    if (this.gazeRing) {
                        this.gazeRing.visible = false;
                    }
                }
            });

            controller.userData.hoveredObject = null;
            controller.userData.isActive = false;

            const lineClone = linesHelper.clone();
            lineClone.material = lineClone.material.clone();
            controller.add(lineClone);
            controller.userData.line = lineClone;

            controller.addEventListener('selectstart' as any, this.handleSelectStart);
            controller.addEventListener('selectend' as any, this.handleSelectEnd);

            controller.addEventListener('squeezestart' as any, () => {
                this.toggleControllerActive(controller);
            });

            controller.addEventListener('disconnected' as any, () => {
                this.removeGamepad(controller);
            });
        });

        xrManager.addEventListener('sessionstart' as any, () => {
            this.isUsingGazeController = false;
            if (this.gazeRing) {
                this.gazeRing.visible = false;
            }
            if (this.camera?.cameraGroup) {
                this.controllerGrips.forEach((grip) => {
                    this.camera.cameraGroup.add(grip);
                });
            }
        });

        xrManager.addEventListener('sessionend' as any, () => {
            this.isUsingGazeController = false;
            if (this.gazeRing) {
                this.gazeRing.visible = false;
            }
            if (this.camera?.cameraGroup) {
                this.controllerGrips.forEach((grip) => {
                    this.camera.cameraGroup.remove(grip);
                });
            }
        });

        this.setControllerActive(this.controllers[0]);
    }

    public hideController(controller: Object3D): void {
        if (controller.userData.line) {
            controller.userData.line.visible = false;
        }

        const handedness = controller.userData.handedness;

        if (handedness === 'left') {
            const leftGrip = this.controllerGrips.find((grip) => grip.userData.handedness === 'left');

            if (leftGrip) {
                leftGrip.traverse((child) => {
                    child.visible = false;
                });
            }
        } else if (handedness === 'right') {
            const rightGrip = this.controllerGrips.find((grip) => grip.userData.handedness === 'right');

            if (rightGrip) {
                rightGrip.traverse((child) => {
                    child.visible = false;
                });
            }
        }
    }

    public showController(controller: Object3D): void {
        if (controller.userData.line) {
            controller.userData.line.visible = true;
        }

        const handedness = controller.userData.handedness;

        if (handedness === 'left') {
            const leftGrip = this.controllerGrips.find((grip) => grip.userData.handedness === 'left');

            if (leftGrip) {
                leftGrip.traverse((child) => {
                    child.visible = true;
                });
            }
        } else if (handedness === 'right') {
            const rightGrip = this.controllerGrips.find((grip) => grip.userData.handedness === 'right');

            if (rightGrip) {
                rightGrip.traverse((child) => {
                    child.visible = true;
                });
            }
        }
    }

    private initializeGamepad(controller: Object3D, gamepad: XRGamepad): void {
        if (gamepad.mapping !== 'xr-standard') {
        }

        this.activeGamepads.set(controller, gamepad);
        this.buttonStates.set(controller, new Array(gamepad.buttons.length).fill(false));
    }

    private removeGamepad(controller: Object3D): void {
        this.activeGamepads.delete(controller);
        this.buttonStates.delete(controller);
    }

    private updateButtonStates(controller: Object3D, gamepad: XRGamepad): void {
        const prevStates = this.buttonStates.get(controller);
        if (!prevStates) return;

        gamepad.buttons.forEach((button, index) => {
            const wasPressed = prevStates[index];
            const isPressed = button.pressed;

            if (isPressed !== wasPressed) {
                prevStates[index] = isPressed;

                let buttonType: string;
                switch (index) {
                    case 0:
                        buttonType = 'trigger';
                        break;
                    case 1:
                        buttonType = 'squeeze';
                        break;
                    case 2:
                        buttonType = 'touchpad';
                        break;
                    case 3:
                        buttonType = 'thumbstick';
                        break;
                    case 5:
                        buttonType = 'b_button';
                        break;
                    default:
                        return;
                }

                this.dispatchEvent({
                    type: 'gamepadButton',
                    button: buttonType,
                    action: isPressed ? 'start' : 'end',
                    controller,
                } as any);
            }
        });
    }

    private updateAxes(controller: Object3D, gamepad: XRGamepad): void {
        if (gamepad.axes.length >= 4) {
            const thumbstickX = gamepad.axes[2];
            const thumbstickY = gamepad.axes[3];

            if (Math.abs(thumbstickX) > this.AXIS_THRESHOLD || Math.abs(thumbstickY) > this.AXIS_THRESHOLD) {
                this.dispatchEvent({
                    type: 'gamepadThumbstick',
                    x: thumbstickX,
                    y: thumbstickY,
                    controller,
                } as any);
            }
        }
    }

    public isHolding(): boolean {
        return this.isHoldingObject;
    }

    public getHeldObject(): Object3D | null {
        return this.heldObject;
    }

    private onSceneChange(): void {
        this.clearInteractiveObjects();
        this.gazeRing?.setDefault();

        this.controllers.forEach((controller) => {
            setTimeout(() => {
                const pointerClone = pointer.clone();
                controller.userData.pointer = pointerClone;
                controller.userData.pointer.visible = false;
                this.renderer.scene.add(pointerClone);
            });
        });
    }

    public addInteractiveObject(
        object: Object3D,
        priority: number,
        callbacks: {
            onHoverStart?: (controller: Object3D, intersection: IntersectionResult) => void;
            onHoverEnd?: (controller: Object3D, intersection: IntersectionResult) => void;
            onHover?: (controller: Object3D, intersection: IntersectionResult) => void;
            onSelectStart?: (controller: Object3D, intersection: IntersectionResult) => void;
            onSelectEnd?: (controller: Object3D, intersection: IntersectionResult) => void;
            onRotate?: (rotation: Quaternion, controller: Object3D, intersection: IntersectionResult) => void;
        },
        addChildren: boolean = false,
    ) {
        this.interactiveObjects.push({ object, priority, ...callbacks });

        const wrappedCallbacks = { ...callbacks };

        if (addChildren) {
            object.traverse((child: Object3D) => {
                if (child !== object) {
                    this.interactiveObjects.push({
                        object: child,
                        priority,
                        ...callbacks,
                    });
                }
            });
        }

        this.interactiveObjects.sort((a, b) => b.priority - a.priority);
    }

    private getPointedObject(controller: Object3D): any {
        this.tempMatrix.identity().extractRotation(controller.matrixWorld);
        this.raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
        this.raycaster.ray.direction.set(0, 0, -1).applyMatrix4(this.tempMatrix);

        const objectsToTest = this.interactiveObjects
            .filter((io) => io && io.object && io.object.userData && !io.object.userData.isNonInteractive)
            .map((io) => io.object);

        const intersections = this.raycaster.intersectObjects(objectsToTest, true);

        this.raycaster.firstHitOnly = true;
        this.raycaster.far = 10;

        if (intersections.length === 0) {
            return null;
        }

        for (const inter of intersections) {
            const foundIO = this.interactiveObjects.find((io) => {
                if (!io || !io.object) return false;

                return (
                    inter.object === io.object ||
                    io.object === inter.object.parent ||
                    io.object === inter.object.parent?.parent
                );
            });

            if (foundIO) {
                return {
                    point: inter.point.clone(),
                    distance: inter.distance,
                    object: foundIO,
                    rawIntersection: inter,
                };
            }
        }

        return null;
    }

    private createIntersectionResult(intersection: any): IntersectionResult {
        return {
            point: intersection.point || new Vector3(),
            distance: intersection.distance || 0,
            object: intersection?.rawIntersection?.object || intersection?.object?.object || null,
        };
    }

    private setControllerActive(controller: Object3D): void {
        if (this.activeController && this.activeController !== controller) {
            this.activeController.userData.isActive = false;
            this.activeController.userData.line.material.color = this.INACTIVE_COLOR;
        }

        this.activeController = controller;
        controller.userData.isActive = true;
        controller.userData.line.material.color = this.ACTIVE_COLOR;

        this.dispatchEvent({
            type: 'controllerActivated',
            controller,
        } as any);
    }

    private updateGazeInteraction() {
        if (!this.isUsingGazeController || !this.gazeRing || !this.gazeController) return;

        const intersection = this.getPointedObject(this.gazeController);

        if (this.gazeController.userData.hoveredObject?.object !== intersection?.object?.object) {
            if (this.gazeController.userData.hoveredObject) {
                const previousHovered = this.gazeController.userData.hoveredObject;
                previousHovered.onHoverEnd?.(
                    this.gazeController,
                    this.createIntersectionResult(
                        intersection || {
                            point: new Vector3(),
                            distance: 0,
                            object: previousHovered.object,
                            rawIntersection: null,
                        },
                    ),
                );
                this.intersectionClock.stop();
                this.gazeRing.setDefault();
            }

            if (intersection?.object) {
                const intersectionResult = this.createIntersectionResult(intersection);
                intersection.object.onHoverStart?.(this.gazeController, intersectionResult);
                this.intersectionClock.start();
            }

            this.gazeController.userData.hoveredObject = intersection?.object || null;
        }

        if (intersection?.object) {
            const elapsedTime = this.intersectionClock.getElapsedTime();

            if (elapsedTime <= this.GAZE_SELECTION_TIME) {
                const progress = elapsedTime / this.GAZE_SELECTION_TIME;
                this.gazeRing.updateProgress(progress);
                (this.gazeRing.material as MeshBasicMaterial).color = this.ACTIVE_COLOR;

                const intersectionResult = this.createIntersectionResult(intersection);
                intersection.object.onHover?.(this.gazeController, intersectionResult);
            }

            if (elapsedTime >= this.GAZE_SELECTION_TIME) {
                const event = { target: this.gazeController };
                this.onSelectStart(event);
                setTimeout(() => this.onSelectEnd(event), 100);

                (this.gazeRing.material as MeshBasicMaterial).color = this.INACTIVE_COLOR;
                this.gazeRing.setDefault();
                this.intersectionClock.stop();
            }
        }
    }

    public isObjectGrabbed(object: Object3D): boolean {
        return [...this.grabbedObjects.values()].some((go) => go.object === object);
    }

    public getGrabbingController(object: Object3D): Object3D | null {
        for (const [controller, grabbedObject] of this.grabbedObjects.entries()) {
            if (grabbedObject.object === object) {
                return controller;
            }
        }
        return null;
    }

    public forceReleaseAll(): void {
        [...this.grabbedObjects.keys()].forEach((controller) => {
            this.releaseObject(controller);
        });
    }

    public update() {
        const deltaTime = this.clock.getDelta();

        this.activeGamepads.forEach((gamepad, controller) => {
            this.updateButtonStates(controller, gamepad);
            this.updateAxes(controller, gamepad);
        });

        let anyIntersection = false;

        this.grabbedObjects.forEach((grabbedObject, controller) => {
            this.updateGrabbedObjectPosition(controller);

            if (controller.userData?.isVirtualMouseController && this.camera) {
                const moveDirection = new Vector3();

                const forward = new Vector3();
                this.camera.instance.getWorldDirection(forward);
                forward.y = 0;
                forward.normalize();

                const right = new Vector3();
                right.crossVectors(this.camera.instance.up, forward).negate();

                if (this.keyStates.get('w')) moveDirection.add(forward);
                if (this.keyStates.get('s')) moveDirection.sub(forward);
                if (this.keyStates.get('d')) moveDirection.add(right);
                if (this.keyStates.get('a')) moveDirection.sub(right);
                if (this.keyStates.get('e')) moveDirection.y += 1;
                if (this.keyStates.get('q')) moveDirection.y -= 1;

                if (moveDirection.lengthSq() > 0) {
                    moveDirection.normalize();
                    const moveOffset = moveDirection.multiplyScalar(this.keyboardMoveSpeed * deltaTime);

                    if (grabbedObject.mode === 'physics' && grabbedObject.physicsBody) {
                        const bodyPos = grabbedObject.physicsBody.position;
                        grabbedObject.physicsBody.position.set(
                            bodyPos.x + moveOffset.x,
                            bodyPos.y + moveOffset.y,
                            bodyPos.z + moveOffset.z,
                        );
                    } else {
                        grabbedObject.object.position.add(moveOffset);
                    }
                }
            }
        });

        this.controllers.forEach((controller) => {
            const currentTime = performance.now();
            const lastTime = this.controllerLastUpdateTime.get(controller);

            if (lastTime) {
                const deltaTime = (currentTime - lastTime) / 1000;

                if (deltaTime > 0) {
                    const currentPos = controller.getWorldPosition(new Vector3());
                    const previousPos = this.controllerPreviousPositions.get(controller);

                    if (previousPos) {
                        const velocity = currentPos.clone().sub(previousPos).divideScalar(deltaTime);

                        controller.userData.velocity = velocity;
                    }

                    this.controllerPreviousPositions.set(controller, currentPos.clone());
                }
            } else {
                this.controllerPreviousPositions.set(controller, controller.getWorldPosition(new Vector3()));
            }

            this.controllerLastUpdateTime.set(controller, currentTime);

            if (!this.renderer.webGLRenderer.xr.isPresenting) {
                return;
            }

            controller.userData.line.material.color = controller.userData.isActive
                ? this.ACTIVE_COLOR
                : this.INACTIVE_COLOR;

            const intersection = this.getPointedObject(controller);

            if (controller === this.activeController) {
                if (controller.userData.hoveredObject?.object !== intersection?.object?.object) {
                    const previousHovered = controller.userData.hoveredObject;
                    if (previousHovered) {
                        previousHovered.onHoverEnd?.(
                            controller,
                            this.createIntersectionResult(
                                intersection || {
                                    point: new Vector3(),
                                    distance: 0,
                                    object: previousHovered.object,
                                    rawIntersection: null,
                                },
                            ),
                        );
                    }

                    if (intersection?.object) {
                        const intersectionResult = this.createIntersectionResult(intersection);
                        intersection.object.onHoverStart?.(controller, intersectionResult);
                    }

                    controller.userData.hoveredObject = intersection?.object || null;
                }

                if (intersection?.object) {
                    const intersectionResult = this.createIntersectionResult(intersection);
                    intersection.object.onHover?.(controller, intersectionResult);
                }
            }

            if (intersection) {
                anyIntersection = true;
                if (controller.userData.isActive) {
                    controller.userData.line.material.color = this.INTERSECTION_COLOR;
                }

                if (controller.userData.pointer) {
                    controller.userData.pointer.visible = controller === this.activeController;
                    if (controller === this.activeController) {
                        controller.userData.pointer.position.copy(intersection.point);
                    }
                }
            } else {
                if (controller.userData.pointer) {
                    controller.userData.pointer.visible = false;
                }
            }
        });

        this.updateGazeInteraction();

        if (this.config) {
            this.config.skipLegacyIntersection = anyIntersection;
        }
    }

    public toggleControllerActive(controller: Object3D): void {
        if (controller === this.activeController) {
            this.deactivateController();
        } else {
            this.setControllerActive(controller);
        }
    }

    public setXrControllers(xrControllers: XrControllers) {
        this.xrControllers = xrControllers;
    }

    private onSelectStart(event: any) {
        const controller = event.target as Object3D;

        if (controller !== this.activeController && !this.isUsingGazeController) {
            this.setControllerActive(controller);
            return;
        }

        const hovered = controller.userData.hoveredObject;
        if (hovered) {
            this.selectedObjects.set(controller, hovered);
            const intersection = this.getPointedObject(controller);
            if (intersection) {
                const intersectionResult = this.createIntersectionResult(intersection);
                hovered.onSelectStart?.(controller, intersectionResult);
            } else {
                hovered.onSelectStart?.(controller);
            }
        }
    }

    private onSelectEnd(event: any) {
        const controller = event.target as Object3D;

        if (controller !== this.activeController && !this.isUsingGazeController) {
            return;
        }

        const selected = this.selectedObjects.get(controller);
        if (selected) {
            const intersection = this.getPointedObject(controller);
            const intersectionResult = intersection
                ? this.createIntersectionResult(intersection)
                : {
                    point: new Vector3(),
                    distance: 0,
                    object: selected.object,
                };
            selected.onSelectEnd?.(controller, intersectionResult);
            this.selectedObjects.delete(controller);
        }
    }

    public removeInteractiveObject(object: Object3D) {
        const index = this.interactiveObjects.findIndex((io) => io.object === object);
        if (index !== -1) {
            this.controllers.forEach((controller) => {
                const hoveredObject = this.hoveredObjects.get(controller);
                if (hoveredObject?.object === object) {
                    const intersection = this.getPointedObject(controller);
                    const intersectionResult = intersection
                        ? this.createIntersectionResult(intersection)
                        : {
                            point: new Vector3(),
                            distance: 0,
                            object: hoveredObject.object,
                        };
                    hoveredObject.onHoverEnd?.(controller, intersectionResult);
                    this.hoveredObjects.delete(controller);
                }

                const selectedObject = this.selectedObjects.get(controller);
                if (selectedObject?.object === object) {
                    const intersection = this.getPointedObject(controller);
                    const intersectionResult = intersection
                        ? this.createIntersectionResult(intersection)
                        : {
                            point: new Vector3(),
                            distance: 0,
                            object: selectedObject.object,
                        };
                    selectedObject.onSelectEnd?.(controller, intersectionResult);
                    this.selectedObjects.delete(controller);
                }
            });

            this.interactiveObjects.splice(index, 1);
        }
    }

    public clearInteractiveObjects(): void {
        this.controllers.forEach((controller) => {
            const hovered = this.hoveredObjects.get(controller);
            if (hovered) {
                const intersection = this.getPointedObject(controller);
                const intersectionResult = intersection
                    ? this.createIntersectionResult(intersection)
                    : {
                        point: new Vector3(),
                        distance: 0,
                        object: hovered.object,
                    };
                hovered.onHoverEnd?.(controller, intersectionResult);
                this.hoveredObjects.delete(controller);
            }

            const selected = this.selectedObjects.get(controller);
            if (selected) {
                const intersection = this.getPointedObject(controller);
                const intersectionResult = intersection
                    ? this.createIntersectionResult(intersection)
                    : {
                        point: new Vector3(),
                        distance: 0,
                        object: selected.object,
                    };
                selected.onSelectEnd?.(controller, intersectionResult);
                this.selectedObjects.delete(controller);
            }
        });

        this.interactiveObjects = [];
        if (this.isUsingGazeController) {
            this.intersectionClock.stop();
            this.gazeRing?.setDefault();
        }
    }

    public getActiveController(): Object3D | null {
        return this.activeController;
    }

    public deactivateController(): void {
        if (this.activeController) {
            this.activeController.userData.isActive = false;
            this.activeController.userData.line.material.color = this.INACTIVE_COLOR;
            this.activeController = null;

                    this.dispatchEvent({
                        type: 'controllerDeactivated',
                    } as any);        }
    }

    public checkPhysicsIntersection(bodyA: Body, bodyB: Body): boolean {
        if (!bodyA || !bodyB) return false;

        const world = bodyA.world || bodyB.world;
        if (!world) return false;
        for (let i = 0; i < world.contacts.length; i++) {
            const contact = world.contacts[i];
            if ((contact.bi === bodyA && contact.bj === bodyB) || (contact.bi === bodyB && contact.bj === bodyA)) {
                return true;
            }
        }

        return false;
    }

    public checkPhysicsContainment(container: Body, object: Body): boolean {
        if (!container || !object) return false;
        container.updateAABB();
        object.updateAABB();
        return (
            object.aabb.lowerBound.x >= container.aabb.lowerBound.x &&
            object.aabb.lowerBound.y >= container.aabb.lowerBound.y &&
            object.aabb.lowerBound.z >= container.aabb.lowerBound.z &&
            object.aabb.upperBound.x <= container.aabb.upperBound.x &&
            object.aabb.upperBound.y <= container.aabb.upperBound.y &&
            object.aabb.upperBound.z <= container.aabb.upperBound.z
        );
    }

    public getPhysicsIntersections(body: Body): Body[] {
        if (!body || !body.world) return [];

        const intersectingBodies: Body[] = [];
        const world = body.world;

        for (let i = 0; i < world.contacts.length; i++) {
            const contact = world.contacts[i];
            if (contact.bi === body && !intersectingBodies.includes(contact.bj)) {
                intersectingBodies.push(contact.bj);
            } else if (contact.bj === body && !intersectingBodies.includes(contact.bi)) {
                intersectingBodies.push(contact.bi);
            }
        }

        return intersectingBodies;
    }

    public checkInsideFlask(flask: Body, object: Body): boolean {
        if (!flask || !object) return false;
        const boxSize = { width: 0.8, height: 1.0, depth: 0.8 };
        const wallThickness = 0.1;
        const flaskPos = flask.position;
        const objectPos = object.position;
        const inX = Math.abs(objectPos.x - flaskPos.x) < boxSize.width / 2 - wallThickness;
        const inZ = Math.abs(objectPos.z - flaskPos.z) < boxSize.depth / 2 - wallThickness;
        const aboveBottom = objectPos.y > flaskPos.y - boxSize.height / 2 + wallThickness;
        const belowTop = objectPos.y < flaskPos.y + boxSize.height / 2;

        return inX && inZ && aboveBottom && belowTop;
    }

    public isPointInsideBody(point: Vec3, body: Body): boolean {
        if (!body || !body.world) return false;
        body.updateAABB();
        if (
            point.x < body.aabb.lowerBound.x ||
            point.x > body.aabb.upperBound.x ||
            point.y < body.aabb.lowerBound.y ||
            point.y > body.aabb.upperBound.y ||
            point.z < body.aabb.lowerBound.z ||
            point.z > body.aabb.upperBound.z
        ) {
            return false;
        }
        return true;
    }

    public requestHapticPulse(controller: Object3D, intensity: number = 1.0, duration: number = 100): void {
        const xrInputSource = (controller as any).inputSource;
        if (xrInputSource?.gamepad?.hapticActuators?.[0]) {
            xrInputSource.gamepad.hapticActuators[0].pulse(intensity, duration).catch((error: any) => {});
        }
    }
}
